import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { MessageSquare, Phone, RefreshCw, User, UserCheck, AlertCircle } from 'lucide-react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { toast } from '../../hooks/use-toast';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtTime = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleString('en-IN', { dateStyle: 'short', timeStyle: 'short' });
  } catch { return iso; }
};

const AdminInbound = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hours, setHours] = useState(24);
  const token = localStorage.getItem('admin_token');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/whatsapp/admin/inbound-today?hours=${hours}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setData(r.data);
    } catch (e) {
      toast({ title: 'Failed to load', description: e.response?.data?.detail || '', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [hours, token]);

  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="admin-inbound-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <MessageSquare className="text-emerald-600" /> Inbound WhatsApp
          </h2>
          <p className="text-sm text-gray-600 mt-1">
            Everyone who messaged your RentKollect business number in the selected window.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={hours}
            onChange={(e) => setHours(parseInt(e.target.value, 10))}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white"
            data-testid="inbound-window"
          >
            <option value={6}>Last 6 hours</option>
            <option value={24}>Last 24 hours</option>
            <option value={72}>Last 3 days</option>
            <option value={168}>Last 7 days</option>
          </select>
          <Button onClick={load} variant="outline" size="sm" data-testid="inbound-refresh">
            <RefreshCw size={14} className="mr-1" /> Refresh
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-3 mb-6">
        <Stat label="Unique senders" value={data?.unique_senders ?? '—'} tint="emerald" />
        <Stat label="Total messages" value={data?.total_messages ?? '—'} tint="blue" />
        <Stat label="Window" value={`${data?.window_hours ?? hours} h`} tint="indigo" />
      </div>

      {loading ? (
        <div className="flex justify-center py-20">
          <div className="w-10 h-10 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (data?.senders?.length || 0) === 0 ? (
        <Card className="p-10 text-center text-gray-500" data-testid="inbound-empty">
          <MessageSquare size={36} className="mx-auto text-gray-300 mb-3" />
          <p>No inbound messages in this window yet.</p>
          <p className="text-xs mt-1 text-gray-400">
            People who message <strong>+91 98219 22296</strong> will appear here.
          </p>
        </Card>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-x-auto">
          <table className="w-full" data-testid="inbound-table">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="p-3 text-left text-xs font-semibold text-gray-600 uppercase">Sender</th>
                <th className="p-3 text-left text-xs font-semibold text-gray-600 uppercase">Last message</th>
                <th className="p-3 text-left text-xs font-semibold text-gray-600 uppercase">Messages</th>
                <th className="p-3 text-left text-xs font-semibold text-gray-600 uppercase">Last seen</th>
              </tr>
            </thead>
            <tbody>
              {data.senders.map((s) => (
                <tr key={s.phone} className="border-b hover:bg-gray-50" data-testid={`inbound-row-${s.phone}`}>
                  <td className="p-3">
                    <div className="flex items-start gap-2">
                      <span className="w-8 h-8 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center text-sm font-semibold flex-shrink-0">
                        {(s.matched_user?.name || s.matched_tenant?.name || s.phone || '?').charAt(0).toUpperCase()}
                      </span>
                      <div className="min-w-0">
                        <p className="font-medium text-gray-900 truncate">
                          {s.matched_user?.name || s.matched_tenant?.name || (
                            <span className="text-gray-500 italic">Unknown sender</span>
                          )}
                        </p>
                        <p className="text-xs text-gray-500 flex items-center gap-1">
                          <Phone size={10} /> {s.phone}
                        </p>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {s.matched_user && (
                            <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded">
                              <UserCheck size={9} /> {s.matched_user.role}
                            </span>
                          )}
                          {s.matched_tenant && (
                            <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 bg-purple-100 text-purple-700 rounded">
                              <User size={9} /> tenant
                            </span>
                          )}
                          {!s.matched_user && !s.matched_tenant && (
                            <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded">
                              <AlertCircle size={9} /> not in DB
                            </span>
                          )}
                          {s.kinds?.includes('image') && (
                            <span className="text-[10px] px-1.5 py-0.5 bg-gray-100 text-gray-700 rounded">image</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="p-3 text-sm text-gray-700 max-w-md truncate">{s.last_text || '—'}</td>
                  <td className="p-3 text-sm text-gray-700 tabular-nums font-medium">{s.count}</td>
                  <td className="p-3 text-xs text-gray-600 whitespace-nowrap">{fmtTime(s.last_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const Stat = ({ label, value, tint }) => {
  const tints = {
    emerald: 'from-emerald-50 to-emerald-100 text-emerald-700',
    blue: 'from-blue-50 to-blue-100 text-blue-700',
    indigo: 'from-indigo-50 to-indigo-100 text-indigo-700',
  };
  return (
    <Card className={`p-4 border-0 bg-gradient-to-br ${tints[tint] || tints.blue}`}>
      <p className="text-xs uppercase tracking-wider font-semibold opacity-70">{label}</p>
      <p className="text-3xl font-bold tabular-nums mt-1">{value}</p>
    </Card>
  );
};

export default AdminInbound;
